import { useEffect, useRef, useState } from 'react';
import { startKotoAmbient, type AmbientHandle } from './kotoAmbient';
import { startJpopAmbient } from './jpopAmbient';

export type AmbientSource =
  | { kind: 'synth'; preset: 'koto' | 'jpop' }
  | { kind: 'file'; key: string; src: string };

const FADE_MS = 320;

export interface UseAmbientAudio {
  on: boolean;
  toggle: () => void;
  available: boolean;
  audioRef: React.RefObject<HTMLAudioElement>;
  /** True iff `audio` has kind === 'file' — UI uses this to render the hidden <audio> tag. */
  isFile: boolean;
  /** The file src, when isFile is true. */
  src?: string;
}

export function useAmbientAudio(audio: AmbientSource | null): UseAmbientAudio {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioFadeRef = useRef<number | null>(null);
  const ctxRef = useRef<AudioContext | null>(null);
  const synthRef = useRef<AmbientHandle | null>(null);

  // Always starts silent on every page load — the visitor opts in by tapping
  // the pet. The choice is intentionally not persisted across loads.
  const [on, setOn] = useState(false);
  const [available, setAvailable] = useState(true);

  // File path
  useEffect(() => {
    if (!audio || audio.kind !== 'file') return;
    const el = audioRef.current;
    if (!el) return;
    const targetVolume = 1.0;

    const clearFade = () => {
      if (audioFadeRef.current !== null) {
        window.clearInterval(audioFadeRef.current);
        audioFadeRef.current = null;
      }
    };
    const fadeTo = (target: number, onDone?: () => void) => {
      clearFade();
      const start = el.volume;
      const startedAt = performance.now();
      audioFadeRef.current = window.setInterval(() => {
        const t = Math.min(1, (performance.now() - startedAt) / FADE_MS);
        el.volume = start + (target - start) * t;
        if (t >= 1) { clearFade(); onDone?.(); }
      }, 16);
    };

    if (on) {
      el.volume = 0;
      const p = el.play();
      if (p && typeof p.catch === 'function') {
        p.then(() => fadeTo(targetVolume)).catch(() => {
          const retry = () => {
            el.play().then(() => fadeTo(targetVolume)).catch(() => { /* give up */ });
            window.removeEventListener('pointerdown', retry, true);
            window.removeEventListener('keydown', retry, true);
          };
          window.addEventListener('pointerdown', retry, { once: true, capture: true });
          window.addEventListener('keydown', retry, { once: true, capture: true });
        });
      } else {
        fadeTo(targetVolume);
      }
    } else if (!el.paused) {
      fadeTo(0, () => { el.pause(); });
    }
    return clearFade;
  }, [on, audio]);

  // Synth path
  useEffect(() => {
    if (!audio || audio.kind !== 'synth') return;
    const targetVolume = 1.0;
    const ensureCtx = (): AudioContext | null => {
      if (ctxRef.current) return ctxRef.current;
      const C: typeof AudioContext | undefined =
        window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!C) { setAvailable(false); return null; }
      try {
        ctxRef.current = new C();
        return ctxRef.current;
      } catch {
        setAvailable(false);
        return null;
      }
    };

    let retry: (() => void) | null = null;

    if (on) {
      const ctx = ensureCtx();
      if (!ctx) return;
      if (ctx.state === 'suspended') ctx.resume().catch(() => {});
      if (!synthRef.current) {
        if (audio.preset === 'koto') synthRef.current = startKotoAmbient(ctx, ctx.destination);
        else if (audio.preset === 'jpop') synthRef.current = startJpopAmbient(ctx, ctx.destination);
      }
      synthRef.current?.setVolume(targetVolume);
      // iOS honours resume() only inside a user gesture; this effect runs after
      // the toggle's click handler returns. Retry on the next interaction.
      if (ctx.state === 'suspended') {
        retry = () => {
          ctx.resume().catch(() => {});
          if (retry) {
            window.removeEventListener('pointerdown', retry, true);
            window.removeEventListener('keydown', retry, true);
          }
        };
        window.addEventListener('pointerdown', retry, { once: true, capture: true });
        window.addEventListener('keydown', retry, { once: true, capture: true });
      }
    } else if (synthRef.current) {
      synthRef.current.stop();
      synthRef.current = null;
      // Drop the context once the stop fade has run: iOS binds it to the
      // ring/silent state at creation, so a fresh context on the next ON
      // reflects the current switch — letting the user recover sound by
      // toggling the pet off and on, with no page reload.
      const dyingCtx = ctxRef.current;
      ctxRef.current = null;
      if (dyingCtx) window.setTimeout(() => { dyingCtx.close().catch(() => {}); }, 900);
    }

    return () => {
      if (retry) {
        window.removeEventListener('pointerdown', retry, true);
        window.removeEventListener('keydown', retry, true);
      }
    };
  }, [on, audio]);

  // Cleanup
  useEffect(() => () => {
    if (synthRef.current) {
      synthRef.current.stop();
      synthRef.current = null;
    }
    if (ctxRef.current) {
      ctxRef.current.close().catch(() => {});
      ctxRef.current = null;
    }
  }, []);

  return {
    on,
    toggle: () => setOn(v => !v),
    available,
    audioRef,
    isFile: audio?.kind === 'file',
    src: audio?.kind === 'file' ? audio.src : undefined,
  };
}
